<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { TableCell, TableRow } from "@/components/ui/table";
  import { Switch } from "@/components/ui/switch";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Checkbox } from "@/components/ui/checkbox";
  import BudgetTablePQuotaCell from "./BudgetTablePQuotaCell.vue";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useBudgetView } from "@composables/budget/useBudgetView";
  import { useBudgetSelection } from "@composables/budget/useBudgetSelection";

  const props = defineProps({
    cAdset: { type: Object, required: true },
    ccampaign: { type: Object, required: true },
  });

  const { currentMCampaign } = storeToRefs(useMCampaignStore());
  const { showConstraints } = useBudgetView();
  const { isSelected, toggleSelection, propagate } = useBudgetSelection();

  const propagateField = (attribute, value) => propagate(props.cAdset.id, attribute, value);

  const isActive = computed(() => props.cAdset.status === "active");

  const campaignInactive = computed(() => props.ccampaign.status !== "active");

  const setActive = (val) => propagate(props.cAdset.id, "status", val ? "active" : "inactive");

  const manualLocked = computed(
    () => !!currentMCampaign.value?.daily_budget_manual || !!props.ccampaign.daily_budget_manual,
  );

  const formatNum = (v, digits = 1) => (v !== undefined && v !== null ? Number(v).toFixed(digits) : "—");

  const pCpi = computed(() => formatNum(props.cAdset.last_bidder_adsets?.p_cpi, 1));
  const alpha = computed(() => formatNum(props.cAdset.last_bidder_adsets?.budget_constraints?.alpha_learning, 1));
  const learningMin = computed(() =>
    formatNum(props.cAdset.last_bidder_adsets?.budget_constraints?.learning_phase_daily_budget_min, 0),
  );
  const learningMax = computed(() =>
    formatNum(props.cAdset.last_bidder_adsets?.budget_constraints?.learning_phase_daily_budget_max, 0),
  );
</script>

<template>
  <TableRow :class="!isActive || campaignInactive ? 'opacity-60' : ''">
    <TableCell class="w-10">
      <Checkbox
        :model-value="isSelected(cAdset.id)"
        @update:model-value="toggleSelection(cAdset.id)" />
    </TableCell>
    <TableCell class="w-14">
      <Tooltip
        :disabled="!campaignInactive"
        content="Campaign is inactive — reactivate it to change ad set status">
        <span class="inline-flex">
          <Switch
            :model-value="isActive"
            :disabled="campaignInactive"
            class="disabled:opacity-100"
            @update:model-value="setActive" />
        </span>
      </Tooltip>
    </TableCell>
    <TableCell :class="['w-[260px]', showConstraints ? 'sticky left-[6rem] z-10 bg-background' : '']">
      <Tooltip
        :disabled="cAdset.name.length <= 30"
        :content="cAdset.name">
        <span class="block truncate text-sm">{{ cAdset.name }}</span>
      </Tooltip>
    </TableCell>
    <TableCell class="w-28 text-right">
      <InputNumber
        :model-value="cAdset.daily_budget"
        :min="0"
        class="h-8 text-right tabular-nums"
        @update:model-value="(value) => propagateField('daily_budget', value)" />
    </TableCell>
    <TableCell class="w-24 text-right tabular-nums text-sm text-muted-foreground">
      <template v-if="cAdset.bidder_suggestion != null">€{{ cAdset.bidder_suggestion }}</template>
      <template v-else>—</template>
    </TableCell>
    <TableCell class="w-24 text-right">
      <Tooltip
        :disabled="!manualLocked"
        content="Remove campaign's manual budget">
        <InputNumber
          :model-value="cAdset.daily_budget_manual"
          :disabled="manualLocked"
          :min="0"
          class="h-8 text-right tabular-nums"
          @update:model-value="(value) => propagateField('daily_budget_manual', value)" />
      </Tooltip>
    </TableCell>
    <TableCell
      v-if="showConstraints"
      class="w-20 text-right">
      <InputNumber
        :model-value="cAdset.daily_budget_min"
        :min="0"
        class="h-7 text-right tabular-nums"
        @update:model-value="(value) => propagateField('daily_budget_min', value)" />
    </TableCell>
    <TableCell
      v-if="showConstraints"
      class="w-20 text-right">
      <InputNumber
        :model-value="cAdset.daily_budget_max"
        :min="0"
        class="h-7 text-right tabular-nums"
        @update:model-value="(value) => propagateField('daily_budget_max', value)" />
    </TableCell>
    <TableCell class="w-16 text-right tabular-nums text-sm">
      {{ pCpi }}
    </TableCell>
    <TableCell class="w-16 text-right tabular-nums text-sm">
      <Tooltip>
        <template #content>
          <div>Min: {{ learningMin }}</div>
          <div>Max: {{ learningMax }}</div>
        </template>
        <span>{{ alpha }}</span>
      </Tooltip>
    </TableCell>
    <TableCell class="w-16 text-center">
      <BudgetTablePQuotaCell :c-adset="cAdset" />
    </TableCell>
  </TableRow>
</template>
